/* eslint-disable */
import React, { useEffect, useRef, useState } from "react";
import { Joystick } from "react-joystick-component";
import SquareMenu from "./squareMenu";
import useAuth from "src/hooks/useAuth";
import Ably from "ably";
import {
  Box,
  Button,
  Typography,
  Card,
  styled,
  useTheme
} from "@mui/material";
import Slider from "@mui/material/Slider";
import { useNavigate } from "react-router-dom";

const estopImg = "/estop.png";
const ablyKey = "ruY5jw.5l4ThA:y75_uB_nFnb51q2WkwE-ntt0VXSabztaWnmXi5zkJ-E";
const machineId = "123";

// 🔑 Priority wrapper for Ably publish
const safePublish = (channel, name, payload) => {
  if (!channel) return;
  if (typeof queueMicrotask === "function") {
    queueMicrotask(() => channel.publish(name, payload));
  } else {
    setTimeout(() => channel.publish(name, payload), 0);
  }
};

// ---------------- Styles ----------------
const OfflineOverlay = styled("div")(() => `
  position: absolute; top: 0; left: 0; width: 100%; height: 100%;
  display: flex; align-items: center; justify-content: center;
  z-index: 3000; background: rgba(0, 0, 0, 0.5);
`);
const OfflineText = styled(Typography)(() => `
  color: red; font-size: 64px; font-weight: bold; text-transform: uppercase; text-align: center;
`);
const CardCamera = styled(Card)(() => `
  position: relative;
  & > img, & > video {
    width: 100%; height: 325px; display: block; position: relative; z-index: 4;
  }
`);
const CameraActions = styled(Box)(({ theme }) => `
  position: absolute; left: 0; top: 0; width: 100%; height: 100%; z-index: 5;
  padding: ${theme.spacing(3)};
  background: linear-gradient(
    to right,
    rgba(0,0,0,0) 0%,
    rgba(0,0,0,0) 0%,
    rgba(0,0,0,0.65) 95%,
    rgba(0,0,0,0.65) 100%
  );
`);
const ButtonDark = styled(Button)(({ theme }) => `
  background: rgba(0,0,0,.6);
  color: ${theme.colors.alpha.trueWhite[100]};
  font-size: ${theme.typography.pxToRem(12)};
  font-weight: normal; white-space: nowrap; height: 32px;
  padding: ${theme.spacing(0, 3)}; margin: ${theme.spacing(0, 1, 1, 0)};
  &:hover { background: rgba(0,0,0,.75); }
`);
const TypographyMain = styled(Typography)(({ theme }) => `
  color: ${theme.colors.alpha.trueWhite[100]};
`);
const TypographyLatency = styled(({ connected, ...other }) => <Typography {...other} />)(
  ({ connected }) => `color: ${connected ? "lime" : "red"};`
);
const EstopOverlay = styled("div")(() => `
  position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
  z-index: 2000; display: flex; align-items: center; justify-content: center; flex-direction: column;
`);
const EstopImage = styled("img")(() => `
  width: 200px; height: 200px; cursor: pointer; user-select: none; pointer-events: auto;
  transition: transform 0.2s; &:hover { transform: scale(1.05); }
`);

function AblyDumperControl() {
  const { user } = useAuth();
  const theme = useTheme();
  const themeColor = theme.palette.primary.main;
  const navigate = useNavigate();

  // UI state
  const [latency, setLatency] = useState(0);
  const [activeUser, setActiveUser] = useState(null);
  const [activeSession, setActiveSession] = useState(null);
  const [connected, setConnected] = useState(false);
  const [status, setStatus] = useState("INIT");
  const [presentUsers, setPresentUsers] = useState([]);
  const [lastStatusTime, setLastStatusTime] = useState(Date.now());
  const [unitOffline, setUnitOffline] = useState(true);
  const [, setDummy] = useState(0);

  // refs
  const gamepadRef = useRef({ leftStick: { x: 0, y: 0 }, rightStick: { x: 0, y: 0 }, buttons: [] });
  const animationFrameRef = useRef(null);

  const modeRef = useRef("Drive");
  const joystickValuesRef = useRef({ x: 0, y: 0 });
  const speedValueRef = useRef(0);

  const ablyRef = useRef(null);
  const pingChannel = useRef(null);
  const pongChannel = useRef(null);
  const connectChannel = useRef(null);
  const statusChannel = useRef(null);

  const uid = useRef(Math.random().toString(36).substring(2, 10));
  const sessionId = useRef(Date.now().toString());

  // ---------------- Gamepad ----------------
  const pollGamepad = () => {
    const gamepads = navigator.getGamepads ? navigator.getGamepads() : [];
    const gp = gamepads[0];
    if (gp) {
      const [lx, ly, rx, ry] = gp.axes;
      const buttons = gp.buttons.map((btn, idx) => ({
        index: idx,
        pressed: btn.pressed,
        value: btn.value
      }));

      const deadzone = 0.2;
      if (Math.abs(ly) > deadzone) {
        const delta = -ly * 2;
        speedValueRef.current = Math.max(0, Math.min(100, Math.round(speedValueRef.current + delta)));
      }

      joystickValuesRef.current = { x: rx, y: -ry };
      gamepadRef.current = { leftStick: { x: lx, y: ly }, rightStick: { x: rx, y: ry }, buttons };
      setDummy(d => d + 1);

      if (buttons[0]?.pressed) sendEstop();
      if (buttons[1]?.pressed) handleModeChange("Exit");
      if (buttons[2]?.pressed) handleModeChange("Drive");
      if (buttons[3]?.pressed) handleModeChange("T&R");
    }
    animationFrameRef.current = requestAnimationFrame(pollGamepad);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.code === "ArrowUp") {
        speedValueRef.current = Math.min(100, Math.round(speedValueRef.current + 2));
        setDummy(d => d + 1);
      }
      if (e.code === "ArrowDown") {
        speedValueRef.current = Math.max(0, Math.round(speedValueRef.current - 2));
        setDummy(d => d + 1);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // ---------------- Messaging ----------------
  const sendMessage = (evt, extra = {}) => {
    safePublish(connectChannel.current, "connect", {
      evt,
      uid: uid.current,
      user_name: user?.name || "anon",
      sessionid: sessionId.current,
      ...extra
    });
    if (evt === 0) navigate('/extended-sidebar/dashboards');
  };
  const sendEstop = () => sendMessage(5);

  const sendPong = (heartbeat = null) => {
    const { x, y } = joystickValuesRef.current;
    const speedValue = speedValueRef.current;
    const mode = modeRef.current;
    const normX = x > 0.2 ? 1 : x < -0.2 ? -1 : 0;
    const normY = y > 0.2 ? 1 : y < -0.2 ? -1 : 0;

    let payload = {
      relays: Array(9).fill(false),
      drive: [0, 0, 0, 0],
      mode,
      speed: speedValue,
      safety: true,
      session_id: sessionId.current,
      uid: uid.current,
      user_name: user?.name
    };

    payload.relays[0] = true;

    if (mode !== "Drive") payload.drive = [0, 0, speedValue, 0];
    if (mode === "Drive") {
      if (normY === 1) { payload.relays[6] = true; payload.relays[5] = true; payload.drive[2] = speedValue; }
      if (normY === -1){ payload.relays[6] = true; payload.relays[7] = true; payload.drive[2] = speedValue; }
      if (normX === 1)  payload.drive[0] = 30;
      if (normX === -1) payload.drive[1] = 30;
    }
    if (mode === "T&R") {
      if (normY === 1)  { payload.relays[1] = true; payload.relays[4] = true; }
      if (normY === -1) { payload.relays[1] = true; payload.relays[8] = true; }
      if (normX === 1)  { payload.relays[1] = true; payload.relays[2] = true; }
      if (normX === -1) { payload.relays[1] = true; payload.relays[3] = true; }
    }
    if (heartbeat) payload.heartbeat = heartbeat;
    safePublish(pongChannel.current, "pong", payload);
  };

  const handleMove = (event) => {
    joystickValuesRef.current = { x: event.x, y: event.y };
    setDummy((d) => d + 1);
  };
  const handleStop = () => {
    joystickValuesRef.current = { x: 0, y: 0 };
    setDummy((d) => d + 1);
  };
  const handleModeChange = (newMode) => {
    modeRef.current = newMode;
    setDummy((d) => d + 1);
    if (newMode !== "Drive") joystickValuesRef.current = { x: 0, y: 0 };
    if (newMode === "Exit") sendMessage(0);
  };

  // ---------------- Ably ----------------
  useEffect(() => {
    ablyRef.current = new Ably.Realtime({ key: ablyKey, recover: (last, cb) => cb(true) });
    statusChannel.current = ablyRef.current.channels.get(`dumper/${machineId}/status`);
    pingChannel.current = ablyRef.current.channels.get(`dumper/${machineId}/ping`);
    pongChannel.current = ablyRef.current.channels.get(`dumper/${machineId}/pong`);
    connectChannel.current = ablyRef.current.channels.get(`dumper/${machineId}/connect`);

    ablyRef.current.connection.on("connected", () => sendMessage(8));

    statusChannel.current.subscribe((msg) => {
      queueMicrotask(() => {
        try {
          const data = JSON.parse(new TextDecoder("utf-8").decode(msg.data));
          setActiveUser(data.user_name);
          setActiveSession(data.session_id || null);
          setStatus(data.status || "UNKNOWN");
          setPresentUsers(Array.isArray(data.present_users)
            ? data.present_users.map((u) => (typeof u === "object" ? u.user_name : u))
            : []);
          if (data.rtt) { setLatency(data.rtt); setConnected(true); }
          setUnitOffline(false);
          setLastStatusTime(Date.now());
        } catch (e) { console.error("Bad status decode", e); }
      });
    });

    pingChannel.current.subscribe((msg) => {
      queueMicrotask(() => {
        try {
          const pingData = JSON.parse(new TextDecoder("utf-8").decode(msg.data));
          if (pingData.uid === uid.current && pingData.session_id === sessionId.current) {
            setConnected(true);
            setLatency(pingData.latency || 0);
            sendPong(pingData.heartbeat);
          }
        } catch (e) { console.error("Failed to decode ping data:", e); }
      });
    });

    const presenceInterval = setInterval(() => sendMessage(2), 5000);
    return () => { clearInterval(presenceInterval); ablyRef.current.close(); };
  }, [user]);

  // ---------------- Render ----------------
  const isWeAreActive = activeUser === user?.name && activeSession === sessionId.current;
  const noUser = status === "NO_USER_CONNECTED";
  const { x, y } = joystickValuesRef.current;
  const speedValue = speedValueRef.current;
  const mode = modeRef.current;

  return (
    <div style={{ height: "100vh", width: "100vw", overflow: "hidden", padding: "5px", boxSizing: "border-box", display: "flex", flexDirection: "column" }}>
      <CardCamera style={{ height: "100%", boxSizing: "border-box", position: "relative" }}>
        <CameraActions display="flex" flexDirection="column" justifyContent="space-between">
          <Box display="flex" justifyContent="space-between">
            <Box>
              <div style={{ position: "absolute", top: "15px", left: "15px" }}>
                <SquareMenu setMenu={handleModeChange} />
              </div>
            </Box>
            <Box>
              <ButtonDark size="small">
                {unitOffline ? "Unit Offline" : connected ? "Connected" : "NOT Connected"}
              </ButtonDark>
              <ButtonDark size="small">{mode}</ButtonDark>
            </Box>
            <Box display="flex" alignItems="center" justifyContent="center" flexDirection="column" sx={{ maxWidth: 170 }}>
              <TypographyMain gutterBottom fontWeight="bold" textAlign="center">Stats</TypographyMain>
              <TypographyLatency sx={{ mb: 2 }} textAlign="center" connected={connected}>
                {latency >= 0 ? `${latency} ms` : "No signal"}
              </TypographyLatency>
            </Box>
          </Box>

          <Box sx={{ width: "100%" }} display="flex" alignSelf="flex-end" alignItems="flex-end" justifyContent="space-between">
            {isWeAreActive && connected && !unitOffline && (
              <Box sx={{ height: 150, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", mx: 2 }}>
                <Typography variant="body2" color="white" gutterBottom>{speedValue}%</Typography>
                <Slider
                  orientation="vertical"
                  value={speedValue}
                  onChange={(e, newValue) => {
                    speedValueRef.current = newValue;
                    setDummy((d) => d + 1);
                  }}
                  step={1} min={0} max={100} valueLabelDisplay="auto"
                  sx={{ color: themeColor, height: 120 }}
                />
                <Typography variant="body2" color="white" sx={{ mt: 1 }}>Speed</Typography>
              </Box>
            )}
            {isWeAreActive && connected && !unitOffline && (
              <Joystick
                size={100}
                baseColor="rgba(255, 255, 255, 0.1)"
                stickColor={themeColor}
                move={handleMove}
                stop={handleStop}
              />
            )}
          </Box>
        </CameraActions>

        <video
          muted autoPlay playsInline
          poster="/static/images/media/Background_Img_no_logo.png"
          style={{ width: "100%", height: "100%", objectFit: "cover", boxSizing: "border-box", zIndex: 1 }}
        />

        {!noUser && connected && !unitOffline && (
          <EstopOverlay>
            <EstopImage src={estopImg} alt="E-Stop" onClick={sendEstop} />
            <Typography variant="h6" style={{ color: "white", marginTop: "10px", textAlign: "center" }}>
              X: {x.toFixed(0)}% | Y: {y.toFixed(0)}%
            </Typography>
          </EstopOverlay>
        )}

        {noUser && (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center"
            sx={{ position: "absolute", top: "40%", left: "50%", transform: "translate(-50%, -50%)", background: "rgba(0,0,0,0.7)", padding: 3, borderRadius: 2, zIndex: 10, minWidth: "300px" }}>
            <Typography variant="h5" gutterBottom color="white">No user connected</Typography>
            <Button
              variant="contained" color="primary"
              onClick={() => sendMessage(1, { uid: uid.current, sessionid: sessionId.current, user_name: user?.name || "anon" })}
              sx={{ mb: 2, zIndex: 11 }}
            >
              Connect as {user?.name || "Anon"}
            </Button>
            <Typography variant="subtitle1" color="white">Present Users</Typography>
            <ul style={{ color: "white", textAlign: "left" }}>
              {presentUsers.length > 0 ? presentUsers.map((userName, idx) => <li key={idx}>{userName}</li>) : <li>No users detected</li>}
            </ul>
          </Box>
        )}

        {unitOffline && (
          <OfflineOverlay>
            <OfflineText>Unit Offline</OfflineText>
          </OfflineOverlay>
        )}

        <Box sx={{ position: "absolute", bottom: 10, left: 10, background: "rgba(0,0,0,0.7)", color: "white", padding: 2, borderRadius: 2, maxWidth: 300, fontSize: "12px" }}>
          <Typography variant="subtitle2" gutterBottom>🎮 Gamepad State</Typography>
          <Typography variant="body2">
            Left Stick: X {gamepadRef.current.leftStick.x.toFixed(2)}, Y {gamepadRef.current.leftStick.y.toFixed(2)}
          </Typography>
          <Typography variant="body2">
            Right Stick: X {gamepadRef.current.rightStick.x.toFixed(2)}, Y {gamepadRef.current.rightStick.y.toFixed(2)}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>Buttons:</Typography>
          <ul style={{ margin: 0, padding: "0 0 0 15px" }}>
            {gamepadRef.current.buttons.map(btn => (
              <li key={btn.index} style={{ color: btn.pressed ? "lime" : "gray" }}>
                #{btn.index} {btn.pressed ? "Pressed ✅" : "Released"}
              </li>
            ))}
          </ul>
        </Box>
      </CardCamera>
    </div>
  );
}

export default AblyDumperControl;
